import { getProfile } from '@src/globalStore/profileStore';
import Dexie, { Table } from 'dexie';
import { logger } from '../misc/logger';

const now = () => Date.now();

export interface Dashboard {
  id: string;
  profileId: number;
  customerId: number;
  title: string;
  type: string;
  config: object; // dashboard state

  createdAt: number;
  updatedAt: number;
  lastAccessedAt: number;

  // for additional info/status
  meta: object;
}

class DashboardsDB extends Dexie {
  dashboards!: Table<Dashboard>;

  constructor() {
    super('DashboardsDB');
    this.version(1).stores({
      dashboards: '[customerId+profileId+id], id, customerId, profileId, updatedAt, createdAt, lastAccessedAt'
    });
  }
}

export const db = new DashboardsDB();

// insert or update (upsert)
export async function saveDashboard(d: Omit<Dashboard, 'createdAt' | 'updatedAt' | 'lastAccessedAt' | 'profileId' | 'customerId'>) {
  const { customer_id: customerId, profile_id: profileId } = getProfile();

  const existing = await db.dashboards.get([customerId, profileId, d.id]);

  const dashboard: Dashboard = {
    ...d,
    customerId,
    profileId,
    createdAt: existing?.createdAt || now(),
    updatedAt: now(),
    lastAccessedAt: now(),
  };

  await db.dashboards.put(dashboard);
}

// get dashboards for a user+customer
export async function getDashboards() {
  const { customer_id: customerId, profile_id: profileId } = getProfile();

  return db.dashboards
    .where('[customerId+profileId]')
    .equals([customerId, profileId])
    .toArray();
}

// get dashboard by id
export async function getDashboardById( id: string): Promise<Dashboard | undefined> {
  const { customer_id: customerId, profile_id: profileId } = getProfile();
  return db.dashboards.get([customerId, profileId, id]);
}

// delete a dashboard by id
export async function deleteDashboard(id: string) {
  const { customer_id: customerId, profile_id: profileId } = getProfile();
  await db.dashboards.delete([customerId, profileId, id]);
}

// time based cleanup
// NOTE: not based on customer or profile
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
async function cleanOldDashboards() {
  const cutoff = Date.now() - ONE_WEEK_MS;

  const old = await db.dashboards
    .where('lastAccessedAt')
    .below(cutoff)
    .toArray();

  const deleteOps = old.map(d => db.dashboards.delete([d.customerId, d.profileId, d.id]));
  await Promise.all(deleteOps);

  logger.log(`Cleaned up ${old.length} old dashboards older than ${new Date(cutoff).toLocaleString()}.`);

  return old.length;
}

// limit based cleanup
// NOTE: not based on customer or profile
async function cleanDashboardsByLimit(limit: number = 100): Promise<number> {
  const dashboards = await db.dashboards
    .orderBy('lastAccessedAt')
    .toArray();

  if (dashboards.length <= limit) {
    logger.log(`No dashboards cleaned. Total dashboards (${dashboards.length}) are within the limit (${limit}).`);
    return 0;
  }

  const excessDashboards = dashboards.slice(0, dashboards.length - limit);
  const deleteOps = excessDashboards.map(d => db.dashboards.delete(d.id));
  await Promise.all(deleteOps);

  logger.log(`Cleaned up ${excessDashboards.length} dashboards to maintain the limit of ${limit}.`);

  return excessDashboards.length;
}

// init action
export async function initDashboardStorage() {
  try {
    await db.open();
    logger.log('initialized dashboards');
    await cleanOldDashboards();
    await cleanDashboardsByLimit();
  } catch (err) {
    console.error('Dashboard storage failed to initialize:', err);
  }
}

// update last accessed entry
export async function updateLastAccessed(id: string): Promise<void> {
  const { customer_id: customerId, profile_id: profileId } = getProfile();

  const dashboard = await db.dashboards.get([customerId, profileId, id]);
  if (dashboard) {
    dashboard.lastAccessedAt = now();
    await db.dashboards.put(dashboard);
    logger.log(`Updated last accessed for dashboard ID ${id} at ${new Date(dashboard.lastAccessedAt).toLocaleString()}.`);
  } else {
    logger.error(`Dashboard with ID ${id} not found.`);
  }
}
